import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { io } from 'socket.io-client';
import Layout from '../components/Layout';
import RichEditor from '../components/RichEditor';
import { campaignAPI, businessAPI, SOCKET_URL } from '../services/api';
import './Campaigns.css';

const STATUS = {
  draft: { text: 'Taslak', className: 'status-draft' },
  scheduled: { text: 'Zamanlandı', className: 'status-scheduled' },
  sending: { text: 'Gönderiliyor', className: 'status-sending' },
  paused: { text: 'Durduruldu', className: 'status-paused' },
  completed: { text: 'Tamamlandı', className: 'status-completed' },
  failed: { text: 'Başarısız', className: 'status-failed' }
};

const CHANNELS = {
  email: { label: 'E-posta', icon: '✉️' },
  whatsapp: { label: 'WhatsApp', icon: '💬' },
  telegram: { label: 'Telegram', icon: '✈️' }
};

const RECIPIENT_STATUS = {
  pending: 'Bekliyor',
  sent: 'Gönderildi',
  failed: 'Başarısız',
  skipped: 'Atlandı'
};

const CONTACT_WINDOWS = [
  { value: 0, label: 'Hepsine gönder' },
  { value: 7, label: 'Son 7 günde ulaşılanları atla' },
  { value: 30, label: 'Son 30 günde ulaşılanları atla' },
  { value: 90, label: 'Son 90 günde ulaşılanları atla' }
];

const EMPTY_FORM = {
  name: '',
  description: '',
  type: 'email',
  subject: '',
  message: '',
  includeSignature: true,
  signatureId: null,
  emailAccountId: null,
  filters: { mode: 'filters', emails: '', city: '', district: '', category: '', excludeContactedDays: 30 }
};

const formatDate = (value) => (value
  ? new Date(value).toLocaleString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  : '');

const errorMessage = (error, fallback) => error.response?.data?.message || fallback;

function useDebounced(value, ms) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

function StatsBar({ statistics }) {
  const s = statistics || {};
  const total = s.total || 0;
  if (!total) return <div className="stats-bar empty" />;
  const pct = (n) => `${((n || 0) / total) * 100}%`;
  return (
    <div className="stats-bar" title={`${s.sent || 0} gönderildi, ${s.failed || 0} başarısız, ${s.skipped || 0} atlandı, ${s.pending || 0} bekliyor`}>
      <span className="seg seg-sent" style={{ width: pct(s.sent) }} />
      <span className="seg seg-failed" style={{ width: pct(s.failed) }} />
      <span className="seg seg-skipped" style={{ width: pct(s.skipped) }} />
    </div>
  );
}

function CampaignEditor({ initial, meta, cities, categories, onClose, onSaved }) {
  const [form, setForm] = useState(initial.form);
  const [districts, setDistricts] = useState([]);
  const [audience, setAudience] = useState(null);
  const [preview, setPreview] = useState(null);
  const [testTo, setTestTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const editorRef = useRef(null);
  const subjectRef = useRef(null);
  const textareaRef = useRef(null);
  const lastFocused = useRef('body');

  const update = (patch) => setForm((current) => ({ ...current, ...patch }));
  const updateFilters = (patch) => setForm((current) => ({ ...current, filters: { ...current.filters, ...patch } }));

  useEffect(() => {
    if (!form.filters.city) {
      setDistricts([]);
      return;
    }
    businessAPI.getDistricts(form.filters.city)
      .then((response) => setDistricts(response.data.data))
      .catch(() => setDistricts([]));
  }, [form.filters.city]);

  // Hedef kitle sayısı
  const audienceKey = useDebounced(JSON.stringify({ type: form.type, filters: form.filters }), 400);
  useEffect(() => {
    const { type, filters } = JSON.parse(audienceKey);
    campaignAPI.getAudience({ type, filters })
      .then((response) => setAudience(response.data.data))
      .catch(() => setAudience(null));
  }, [audienceKey]);

  // Canlı önizleme (sunucudaki gerçek şablonla)
  const previewKey = useDebounced(JSON.stringify({
    type: form.type, subject: form.subject, message: form.message, includeSignature: form.includeSignature, signatureId: form.signatureId,
    emailAccountId: form.emailAccountId,
    manual: form.type === 'email' && form.filters.mode === 'manual'
  }), 500);
  useEffect(() => {
    campaignAPI.preview(JSON.parse(previewKey))
      .then((response) => setPreview(response.data.data))
      .catch(() => setPreview(null));
  }, [previewKey]);

  const insertVariable = (key) => {
    const token = `{{${key}}}`;
    if (lastFocused.current === 'subject' && subjectRef.current) {
      const input = subjectRef.current;
      const start = input.selectionStart ?? form.subject.length;
      const end = input.selectionEnd ?? start;
      update({ subject: form.subject.slice(0, start) + token + form.subject.slice(end) });
      return;
    }
    if (form.type === 'email') {
      editorRef.current?.insertText(token);
    } else if (textareaRef.current) {
      const area = textareaRef.current;
      const start = area.selectionStart ?? form.message.length;
      const end = area.selectionEnd ?? start;
      update({ message: form.message.slice(0, start) + token + form.message.slice(end) });
    }
  };

  const changeType = (type) => {
    if (type === form.type) return;
    // Kanal değişince mesajı uygun biçime çevir
    const message = type === 'whatsapp'
      ? form.message.replace(/<\/(p|div|h2|li)>/gi, '\n').replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/\n{3,}/g, '\n\n').trim()
      : form.message.split(/\n{2,}/).map((p) => `<p>${p.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/\n/g, '<br>')}</p>`).join('');
    // Belirli adres modu sadece e-postada var
    setForm((current) => ({
      ...current,
      type,
      message,
      filters: type === 'email' ? current.filters : { ...current.filters, mode: 'filters' }
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setBusy(true);
    setFeedback(null);
    try {
      const response = initial.id
        ? await campaignAPI.update(initial.id, form)
        : await campaignAPI.create(form);
      onSaved(response.data.data);
    } catch (error) {
      setFeedback({ type: 'error', text: errorMessage(error, 'Kampanya kaydedilemedi') });
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    setFeedback(null);
    try {
      const response = await campaignAPI.sendTest({ ...form, to: testTo });
      setFeedback({ type: 'success', text: response.data.message });
    } catch (error) {
      setFeedback({ type: 'error', text: errorMessage(error, 'Test gönderilemedi') });
    }
  };

  const channelReady = meta?.channels?.[form.type];
  const defaultSignature = (meta?.signatures || []).find((sig) => sig.isDefault);
  const defaultAccount = (meta?.emailAccounts || []).find((account) => account.isDefault);
  // İmza elle seçilmediyse gönderimde kullanılacak imza: hesaba bağlı imza, yoksa varsayılan imza
  const selectedAccount = (meta?.emailAccounts || []).find((account) => account.id === form.emailAccountId) || defaultAccount;
  const autoSignature = (form.type === 'email' && selectedAccount?.signatureId
    && (meta?.signatures || []).find((sig) => sig.id === selectedAccount.signatureId)) || defaultSignature;
  const manual = form.type === 'email' && form.filters.mode === 'manual';

  return (
    <div className="modal">
      <form className="modal-content editor-modal" onSubmit={handleSave}>
        <div className="editor-header">
          <h2>{initial.id ? 'Kampanyayı Düzenle' : 'Yeni Kampanya'}</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Kapat">×</button>
        </div>

        <div className="editor-body">
          <div className="editor-left">
            <div className="form-group">
              <label>Kampanya adı</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => update({ name: e.target.value })}
                placeholder="Ör. Kadıköy kuaförleri – Ekim tanıtımı"
                required
              />
            </div>

            <div className="form-group">
              <label>Kanal</label>
              <div className="channel-picker">
                {['email', 'whatsapp'].map((type) => (
                  <button
                    type="button"
                    key={type}
                    className={`channel-option ${form.type === type ? 'active' : ''}`}
                    onClick={() => changeType(type)}
                  >
                    <span>{CHANNELS[type].icon} {CHANNELS[type].label}</span>
                    <small className={meta?.channels?.[type] ? 'ok' : 'warn'}>
                      {meta?.channels?.[type] ? 'Hazır' : 'Bağlı değil'}
                    </small>
                  </button>
                ))}
              </div>
              {meta && !channelReady && (
                <small className="hint warn">
                  {form.type === 'email'
                    ? 'E-posta hesabı ayarlanmamış. Taslak kaydedebilirsiniz; göndermek için '
                    : 'WhatsApp bağlı değil. Taslak kaydedebilirsiniz; göndermek için '}
                  <Link to="/settings">Ayarlar</Link> sayfasından bağlanın.
                </small>
              )}
            </div>

            {form.type === 'email' && (meta?.emailAccounts || []).length > 0 && (
              <div className="form-group">
                <label>Gönderen hesap</label>
                <select
                  value={form.emailAccountId || defaultAccount?.id || ''}
                  onChange={(e) => update({ emailAccountId: Number(e.target.value) || null })}
                >
                  {meta.emailAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name}{account.name !== account.address ? ` — ${account.address}` : ''}{account.isDefault ? ' (varsayılan)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {form.type === 'email' && (
              <div className="form-group">
                <label>Konu</label>
                <input
                  ref={subjectRef}
                  type="text"
                  value={form.subject}
                  onFocus={() => { lastFocused.current = 'subject'; }}
                  onChange={(e) => update({ subject: e.target.value })}
                  placeholder="Ör. {{isletme_adi}} için dijital pazarlama teklifi"
                  required
                />
              </div>
            )}

            <div className="form-group">
              <label>Mesaj</label>
              <div className="variable-chips">
                <span>Değişken ekle:</span>
                {(meta?.variables || []).map((v) => (
                  <button
                    type="button"
                    key={v.key}
                    title={`Örnek: ${v.sample}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => insertVariable(v.key)}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
              {form.type === 'email' ? (
                <div onFocus={() => { lastFocused.current = 'body'; }}>
                  <RichEditor
                    ref={editorRef}
                    value={form.message}
                    onChange={(message) => update({ message })}
                    placeholder="Merhaba {{isletme_adi}} ekibi, ..."
                  />
                </div>
              ) : (
                <textarea
                  ref={textareaRef}
                  rows="9"
                  value={form.message}
                  onFocus={() => { lastFocused.current = 'body'; }}
                  onChange={(e) => update({ message: e.target.value })}
                  placeholder="Merhaba {{isletme_adi}}, ..."
                  required
                />
              )}
              <small className="hint">
                Değişken boşsa varsayılan yazmak için: <code>{'{{isletme_adi|Değerli işletme}}'}</code>
              </small>
            </div>

            <div className="form-group">
              <label>İmza</label>
              {meta && !meta.hasSignature ? (
                <small className="hint warn">
                  Henüz imza oluşturmadınız. <Link to="/settings">Ayarlar</Link> sayfasından bir ya da birden fazla imza ekleyebilirsiniz.
                </small>
              ) : (
                <select
                  value={form.includeSignature ? (form.signatureId || '') : 'none'}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'none') update({ includeSignature: false, signatureId: null });
                    else update({ includeSignature: true, signatureId: Number(value) || null });
                  }}
                >
                  <option value="">
                    {form.type === 'email' ? 'Hesabın imzası' : 'Varsayılan imza'}
                    {autoSignature ? ` — ${autoSignature.name}` : ''}
                  </option>
                  {(meta?.signatures || []).map((sig) => (
                    <option key={sig.id} value={sig.id}>{sig.name}{sig.isDefault ? ' (varsayılan)' : ''}</option>
                  ))}
                  <option value="none">İmza ekleme</option>
                </select>
              )}
              {form.type === 'email' && form.includeSignature && (
                <small className="hint">
                  {form.signatureId
                    ? 'Bu kampanya için elle seçildi; gönderen hesabın imzası yerine kullanılır.'
                    : 'Gönderen hesabı değiştirirseniz imza da o hesaba bağlı imzaya geçer (Ayarlar → Gönderen E-posta Hesapları).'}
                </small>
              )}
            </div>

            <h3>Hedef kitle</h3>
            {form.type === 'email' && (
              <div className="audience-mode">
                <button
                  type="button"
                  className={manual ? '' : 'active'}
                  onClick={() => updateFilters({ mode: 'filters' })}
                >
                  Filtreye uyan işletmeler
                </button>
                <button
                  type="button"
                  className={manual ? 'active' : ''}
                  onClick={() => updateFilters({ mode: 'manual' })}
                >
                  Belirli e-posta adresleri
                </button>
              </div>
            )}

            {manual ? (
              <>
                <div className="form-group">
                  <label>E-posta adresleri</label>
                  <textarea
                    rows="5"
                    value={form.filters.emails}
                    onChange={(e) => updateFilters({ emails: e.target.value })}
                    placeholder={'ali@firma.com\nAyşe Kaya <ayse@ornek.com>, info@diger.com'}
                  />
                  <small className="hint">
                    Her satıra bir adres yazın ya da virgülle ayırın (en fazla 500). Adres kayıtlı bir işletmeye aitse
                    {' {{isletme_adi}}'} gibi değişkenler dolar; değilse varsayılan değerler kullanılır.
                  </small>
                </div>

                <div className={`audience-box ${audience?.count === 0 ? 'empty' : ''}`}>
                  <strong>{audience ? audience.count : '…'}</strong> adrese gönderilecek
                  {audience?.matched?.length > 0 && (
                    <small>{audience.matched.length} adres kayıtlı işletmelerle eşleşti: {audience.matched.slice(0, 3).map((m) => m.name).join(', ')}{audience.matched.length > 3 ? ' ...' : ''}</small>
                  )}
                  {audience?.invalid?.length > 0 && (
                    <small className="warn">Geçersiz, atlanacak: {audience.invalid.slice(0, 5).join(', ')}{audience.invalid.length > 5 ? ' ...' : ''}</small>
                  )}
                  {audience?.suppressed?.length > 0 && (
                    <small className="warn">Listeden çıkmış, gönderilmeyecek: {audience.suppressed.join(', ')}</small>
                  )}
                  {audience?.truncated && <small className="warn">500'den fazla adres girildi; ilk 500 kullanılacak.</small>}
                </div>
              </>
            ) : (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>Şehir</label>
                    <select value={form.filters.city} onChange={(e) => updateFilters({ city: e.target.value, district: '' })}>
                      <option value="">Tüm şehirler</option>
                      {cities.map((city) => <option key={city} value={city}>{city}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>İlçe</label>
                    <select value={form.filters.district} onChange={(e) => updateFilters({ district: e.target.value })} disabled={!form.filters.city}>
                      <option value="">Tüm ilçeler</option>
                      {districts.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Kategori</label>
                    <select value={form.filters.category} onChange={(e) => updateFilters({ category: e.target.value })}>
                      <option value="">Tüm kategoriler</option>
                      {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Tekrar iletişim</label>
                  <select
                    value={form.filters.excludeContactedDays}
                    onChange={(e) => updateFilters({ excludeContactedDays: Number(e.target.value) })}
                  >
                    {CONTACT_WINDOWS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
                  </select>
                </div>

                <div className={`audience-box ${audience?.count === 0 ? 'empty' : ''}`}>
                  <strong>{audience ? audience.count : '…'}</strong> işletmeye gönderilecek
                  <small>
                    {form.type === 'email'
                      ? 'E-posta adresi olan ve listeden çıkmamış işletmeler'
                      : 'Telefonu olan işletmeler (WhatsApp kullanmayan numaralar gönderimde atlanır)'}
                  </small>
                  {audience?.sample?.length > 0 && (
                    <div className="audience-sample">
                      {audience.sample.map((b) => b.name).join(', ')}
                      {audience.count > audience.sample.length && ' ...'}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="editor-right">
            <div className="preview-header">
              <h3>Önizleme</h3>
              <small>{form.type === 'email' && form.filters.mode === 'manual' ? 'Kayıtlı işletmesi olmayan bir adres için' : 'Örnek işletme verisiyle'}</small>
            </div>
            {form.type === 'email' ? (
              <div className="email-preview">
                <div className="email-preview-subject">
                  <span>Konu:</span> {preview?.subject || <em>(konu yok)</em>}
                </div>
                <iframe title="E-posta önizleme" sandbox="" srcDoc={preview?.html || ''} />
              </div>
            ) : (
              <div className="whatsapp-preview">
                <div className="wa-bubble">{preview?.text || 'Mesajınız burada görünecek'}</div>
              </div>
            )}

            <div className="test-box">
              <label>Kendine test gönder</label>
              <div className="test-row">
                <input
                  type={form.type === 'email' ? 'email' : 'tel'}
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder={form.type === 'email' ? 'ornek@firma.com' : '+90 5xx xxx xx xx'}
                />
                <button type="button" className="btn-secondary" onClick={handleTest} disabled={!testTo || !channelReady}>
                  Test gönder
                </button>
              </div>
            </div>
          </div>
        </div>

        {feedback && <div className={`notice notice-${feedback.type}`}>{feedback.text}</div>}

        <div className="modal-actions">
          <button type="button" onClick={onClose}>İptal</button>
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? 'Kaydediliyor...' : 'Taslak olarak kaydet'}
          </button>
        </div>
      </form>
    </div>
  );
}

function RecipientsModal({ campaign, onClose }) {
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], total: 0, totalPages: 1 });

  useEffect(() => {
    campaignAPI.getRecipients(campaign.id, { status, page })
      .then((response) => setData({ rows: response.data.data, total: response.data.total, totalPages: response.data.totalPages }))
      .catch(() => setData({ rows: [], total: 0, totalPages: 1 }));
  }, [campaign.id, campaign.statistics, status, page]);

  const stats = campaign.statistics || {};
  const tabs = [
    { key: '', label: 'Tümü', count: stats.total },
    { key: 'sent', label: 'Gönderildi', count: stats.sent },
    { key: 'failed', label: 'Başarısız', count: stats.failed },
    { key: 'skipped', label: 'Atlandı', count: stats.skipped },
    { key: 'pending', label: 'Bekliyor', count: stats.pending }
  ];

  return (
    <div className="modal" onClick={onClose}>
      <div className="modal-content recipients-modal" onClick={(e) => e.stopPropagation()}>
        <div className="editor-header">
          <h2>{campaign.name} — Alıcılar</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Kapat">×</button>
        </div>
        <div className="tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={status === tab.key ? 'active' : ''}
              onClick={() => { setStatus(tab.key); setPage(1); }}
            >
              {tab.label} <span>{tab.count || 0}</span>
            </button>
          ))}
        </div>
        <div className="recipients-table">
          <table>
            <thead>
              <tr>
                <th>İşletme</th>
                <th>{campaign.type === 'email' ? 'E-posta' : 'Telefon'}</th>
                <th>Durum</th>
                <th>Zaman</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 ? (
                <tr><td colSpan="4" className="empty-cell">Kayıt yok</td></tr>
              ) : data.rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    {r.business?.name || (r.businessId ? `Silinmiş işletme #${r.businessId}` : r.address)}
                    {r.business
                      ? <div className="muted">{[r.business.district, r.business.city].filter(Boolean).join(', ')}</div>
                      : !r.businessId && <div className="muted">Kayıtlı işletme değil</div>}
                  </td>
                  <td>{r.address || (campaign.type === 'email' ? r.business?.email : r.business?.phone) || '-'}</td>
                  <td>
                    <span className={`r-status r-${r.status}`}>{RECIPIENT_STATUS[r.status]}</span>
                    {r.error && <div className="muted error-text">{r.error}</div>}
                  </td>
                  <td className="muted">{formatDate(r.sentAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.totalPages > 1 && (
          <div className="pager">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)}>Önceki</button>
            <span>{page} / {data.totalPages}</span>
            <button disabled={page >= data.totalPages} onClick={() => setPage(page + 1)}>Sonraki</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(null);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editor, setEditor] = useState(null);
  const [recipientsFor, setRecipientsFor] = useState(null);
  const [notice, setNotice] = useState(null);

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const response = await campaignAPI.getAll();
      setCampaigns(response.data.data);
    } catch (error) {
      setNotice({ type: 'error', text: errorMessage(error, 'Kampanyalar yüklenemedi') });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMeta = useCallback(() => {
    campaignAPI.getMeta().then((response) => setMeta(response.data.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadCampaigns();
    loadMeta();
    businessAPI.getFilterOptions()
      .then((response) => {
        setCities(response.data.data.cities);
        setCategories(response.data.data.categories);
      })
      .catch(() => {});

    // Gönderim ilerlemesi canlı gelir
    const socket = io(SOCKET_URL, { auth: { token: localStorage.getItem('token') } });
    socket.on('campaign-progress', (update) => {
      setCampaigns((list) => list.map((c) => (c.id === update.id
        ? { ...c, ...update, isRunning: update.status === 'sending' }
        : c)));
      setRecipientsFor((current) => (current && current.id === update.id ? { ...current, ...update } : current));
    });
    return () => socket.disconnect();
  }, [loadCampaigns, loadMeta]);

  const replaceCampaign = (campaign) => {
    setCampaigns((list) => {
      const exists = list.some((c) => c.id === campaign.id);
      return exists ? list.map((c) => (c.id === campaign.id ? campaign : c)) : [campaign, ...list];
    });
  };

  const act = async (fn, successText) => {
    setNotice(null);
    try {
      const response = await fn();
      if (response.data.data) replaceCampaign(response.data.data);
      if (successText || response.data.message) {
        setNotice({ type: 'success', text: successText || response.data.message });
      }
    } catch (error) {
      setNotice({ type: 'error', text: errorMessage(error, 'İşlem başarısız') });
    }
  };

  const handleSend = (campaign) => {
    const count = campaign.statistics?.pending ?? campaign.statistics?.total ?? 0;
    const verb = campaign.status === 'paused' ? 'kalan' : '';
    if (!window.confirm(`"${campaign.name}" kampanyası ${verb} ${count} alıcıya ${CHANNELS[campaign.type]?.label || ''} ile gönderilecek. Onaylıyor musunuz?`)) return;
    act(() => campaignAPI.send(campaign.id));
  };

  const handleDelete = (campaign) => {
    if (!window.confirm(`"${campaign.name}" kampanyası silinsin mi?`)) return;
    act(async () => {
      const response = await campaignAPI.delete(campaign.id);
      setCampaigns((list) => list.filter((c) => c.id !== campaign.id));
      return { data: { message: response.data.message } };
    });
  };

  const openEditor = (campaign) => {
    loadMeta();
    if (!campaign) {
      setEditor({ id: null, form: EMPTY_FORM });
      return;
    }
    setEditor({
      id: campaign.id,
      form: {
        name: campaign.name,
        description: campaign.description || '',
        type: campaign.type === 'telegram' ? 'email' : campaign.type,
        subject: campaign.subject || '',
        message: campaign.message || '',
        includeSignature: campaign.includeSignature !== false,
        signatureId: campaign.signatureId || null,
        emailAccountId: campaign.emailAccountId || null,
        filters: {
          ...EMPTY_FORM.filters,
          ...(campaign.filters || {}),
          emails: Array.isArray(campaign.filters?.emails) ? campaign.filters.emails.join('\n') : (campaign.filters?.emails || '')
        }
      }
    });
  };

  return (
    <Layout>
      <div className="campaigns">
        <div className="page-header">
          <h1>Kampanyalar</h1>
          <button className="btn-primary" onClick={() => openEditor(null)}>
            + Yeni Kampanya
          </button>
        </div>

        {notice && (
          <div className={`notice notice-${notice.type}`}>
            <span>{notice.text}</span>
            <button onClick={() => setNotice(null)} aria-label="Kapat">×</button>
          </div>
        )}

        {loading && campaigns.length === 0 ? (
          <div className="loading">Yükleniyor...</div>
        ) : campaigns.length === 0 ? (
          <div className="empty-state">
            <p>Henüz kampanya oluşturmadınız.</p>
            <button className="btn-primary" onClick={() => openEditor(null)}>İlk kampanyanı oluştur</button>
          </div>
        ) : (
          <div className="campaigns-grid">
            {campaigns.map((campaign) => {
              const status = STATUS[campaign.status] || { text: campaign.status, className: '' };
              const s = campaign.statistics || {};
              const done = (s.sent || 0) + (s.failed || 0) + (s.skipped || 0);
              const channel = CHANNELS[campaign.type] || { label: campaign.type, icon: '' };
              return (
                <div key={campaign.id} className="campaign-card">
                  <div className="campaign-header">
                    <div>
                      <h3>{campaign.name}</h3>
                      <div className="campaign-sub">
                        {channel.icon} {channel.label}
                        {campaign.filters?.mode === 'manual' && <> · Belirli adresler</>}
                        {campaign.subject && <> · <span className="subject" title={campaign.subject}>{campaign.subject}</span></>}
                      </div>
                    </div>
                    <span className={`status-badge ${status.className}`}>{status.text}</span>
                  </div>

                  <StatsBar statistics={s} />
                  <div className="campaign-stats">
                    <div><strong>{s.total || 0}</strong><span>Alıcı</span></div>
                    <div><strong className="c-sent">{s.sent || 0}</strong><span>Gönderildi</span></div>
                    <div><strong className="c-failed">{s.failed || 0}</strong><span>Başarısız</span></div>
                    <div><strong className="c-skipped">{s.skipped || 0}</strong><span>Atlandı</span></div>
                  </div>

                  {campaign.status === 'sending' && (
                    <div className="sending-line">
                      Gönderiliyor… {done} / {s.total || 0}
                    </div>
                  )}
                  {campaign.lastError && campaign.status !== 'sending' && (
                    <div className="campaign-error">{campaign.lastError}</div>
                  )}
                  <div className="campaign-dates">
                    Oluşturuldu {formatDate(campaign.createdAt)}
                    {campaign.completedAt && <> · Bitti {formatDate(campaign.completedAt)}</>}
                  </div>

                  <div className="campaign-actions">
                    {campaign.type === 'telegram' ? (
                      <span className="muted small">Telegram kampanyaları artık desteklenmiyor; kopyalayıp e-posta/WhatsApp ile gönderebilirsiniz.</span>
                    ) : (
                      <>
                        {campaign.status === 'draft' && (
                          <button className="btn-send" onClick={() => handleSend(campaign)}>Gönder</button>
                        )}
                        {campaign.status === 'paused' && (
                          <button className="btn-send" onClick={() => handleSend(campaign)}>Devam Et</button>
                        )}
                        {campaign.status === 'sending' && (
                          <button className="btn-warn" onClick={() => act(() => campaignAPI.cancel(campaign.id))}>Durdur</button>
                        )}
                        {['completed', 'failed'].includes(campaign.status) && s.failed > 0 && (
                          <button className="btn-send" onClick={() => act(() => campaignAPI.retry(campaign.id))}>
                            Başarısızları tekrar dene
                          </button>
                        )}
                      </>
                    )}
                    {campaign.status === 'draft' && campaign.type !== 'telegram' && (
                      <button className="btn-ghost" onClick={() => openEditor(campaign)}>Düzenle</button>
                    )}
                    {campaign.status !== 'draft' && (
                      <button className="btn-ghost" onClick={() => setRecipientsFor(campaign)}>Alıcılar</button>
                    )}
                    <button className="btn-ghost" onClick={() => act(() => campaignAPI.duplicate(campaign.id), 'Kampanya kopyalandı (taslak)')}>
                      Kopyala
                    </button>
                    {campaign.status !== 'sending' && (
                      <button className="btn-ghost danger" onClick={() => handleDelete(campaign)}>Sil</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {editor && (
          <CampaignEditor
            initial={editor}
            meta={meta}
            cities={cities}
            categories={categories}
            onClose={() => setEditor(null)}
            onSaved={(campaign) => {
              replaceCampaign(campaign);
              setEditor(null);
              setNotice({ type: 'success', text: `"${campaign.name}" kaydedildi. ${campaign.statistics?.total || 0} alıcı hazır.` });
            }}
          />
        )}

        {recipientsFor && (
          <RecipientsModal campaign={recipientsFor} onClose={() => setRecipientsFor(null)} />
        )}
      </div>
    </Layout>
  );
}

export default Campaigns;
